import type { Transition } from "framer-motion";
import type { ReactNode } from "react";
import { createContext, useEffect, useMemo, useState } from "react";

import {
  debugExitPresets,
  debugSpringPresets,
  exitPresets,
  reducedMotionPresets,
  type SpringPreset,
  springPresets,
} from "./presets";

export type AnimationMode = "debug" | "normal" | "reduced" | "system";

export interface AnimationContextValue {
  mode: AnimationMode;
  resolvedMode: Exclude<AnimationMode, "system">;
  isDebug: boolean;
  getSpring: (preset: SpringPreset) => Transition;
  getExitSpring: (preset: SpringPreset) => Transition;
}

export const AnimationContext = createContext<AnimationContextValue | null>(
  null
);

const CSS_VARIABLES = {
  normal: {
    "--primaris-sidebar-duration": "200ms",
  },
  debug: {
    "--primaris-sidebar-duration": "800ms",
  },
  reduced: {
    "--primaris-sidebar-duration": "50ms",
  },
} as const;

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia(query);
    setMatches(mediaQuery.matches);

    const handler = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    mediaQuery.addEventListener("change", handler);
    return () => mediaQuery.removeEventListener("change", handler);
  }, [query]);

  return matches;
}

export interface AnimationProviderProps {
  children: ReactNode;
  mode?: AnimationMode;
}

export function AnimationProvider({
  children,
  mode = "system",
}: AnimationProviderProps) {
  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)"
  );

  const resolvedMode = useMemo((): Exclude<AnimationMode, "system"> => {
    if (mode === "system") {
      return prefersReducedMotion ? "reduced" : "normal";
    }
    return mode;
  }, [mode, prefersReducedMotion]);

  const isDebug = resolvedMode === "debug";

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-animation-mode", resolvedMode);

    const variables = CSS_VARIABLES[resolvedMode];
    for (const [key, cssValue] of Object.entries(variables)) {
      root.style.setProperty(key, cssValue);
    }

    return () => {
      root.removeAttribute("data-animation-mode");
      for (const key of Object.keys(variables)) {
        root.style.removeProperty(key);
      }
    };
  }, [resolvedMode]);

  const getSpring = useMemo(
    () =>
      (preset: SpringPreset): Transition => {
        switch (resolvedMode) {
          case "debug":
            return debugSpringPresets[preset];
          case "reduced":
            return reducedMotionPresets[preset];
          default:
            return springPresets[preset];
        }
      },
    [resolvedMode]
  );

  const getExitSpring = useMemo(
    () =>
      (preset: SpringPreset): Transition => {
        switch (resolvedMode) {
          case "debug":
            return debugExitPresets[preset];
          case "reduced":
            return reducedMotionPresets[preset];
          default:
            return exitPresets[preset];
        }
      },
    [resolvedMode]
  );

  const value = useMemo(
    (): AnimationContextValue => ({
      mode,
      resolvedMode,
      isDebug,
      getSpring,
      getExitSpring,
    }),
    [mode, resolvedMode, isDebug, getSpring, getExitSpring]
  );

  return (
    <AnimationContext.Provider value={value}>
      {children}
    </AnimationContext.Provider>
  );
}
